/**
 * Shared utility functions for minimax evaluators
 */

import { GameState } from "../../types";

/**
 * Small bonus added to terminal win score when the victory is a race win.
 * Race wins are preferred over king captures because:
 * - The AI shouldn't "know" which piece is the enemy king
 * - Some captures carry risks (e.g., monkey vs bear has 66% failure)
 */
export const RACE_VICTORY_BONUS = 10;

/**
 * Absolute-score boundary that separates "decided by mate" from "decided by
 * static eval". Terminal wins return ~+100000 (±RACE_VICTORY_BONUS), terminal
 * losses ~-100000. Any |score| > MATE_THRESHOLD is treated as a forced
 * outcome for tie-break and fallback decisions.
 */
export const MATE_THRESHOLD = 90000;

/**
 * Detect whether a PostGame state was won by race (king reached goal row)
 * or by king capture. Returns true if both kings still exist on the board,
 * meaning the game ended by race rather than capture.
 *
 * Only call this when gameState.state === State.PostGame.
 */
export function isRaceVictory(gameState: GameState): boolean {
  let lightKingFound = false;
  let darkKingFound = false;

  for (let row = 0; row < 8; row++) {
    for (let col = 0; col < 5; col++) {
      for (const piece of gameState.board[row][col].Pieces) {
        if (piece.isKing) {
          if (piece.isLight) lightKingFound = true;
          else darkKingFound = true;

          if (lightKingFound && darkKingFound) return true;
        }
      }
    }
  }

  return lightKingFound && darkKingFound;
}
